import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'

import { Link, useSearchParams } from 'react-router-dom'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { useAktion, useGemerkt, useLaden, useScrollMerken } from '../hooks'
import ArtikelAuswahl from '../komponenten/ArtikelAuswahl'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import { rechenbasisTitel } from '../komponenten/RechenbasisStandard'
import ReichweiteRechnen from '../komponenten/ReichweiteRechnen'
import Vermerke, { Pflegeabschnitt } from '../komponenten/Vermerke'
import Beobachtungen from '../komponenten/Beobachtungen'
import {
  MONATE_KURZ,
  datum,
  freitagFolgewocheIso,
  heuteIso,
  letzterDienstagIso,
  menge,
  plusTage,
  stueck,
  tageLesen,
  tageZwischen,
  tonnen,
  wochen,
  zahlLesen,
} from '../format'
import { aktiveFertigartikel, bloecke } from '../hilfen'
import { assistentPfad } from '../pfade'
import { BESTELLSTATUS } from '../texte'
import { RECHENBASEN, TAKTE } from '../typen'
import type {
  Artikel,
  Bestellstatus,
  Bestellung,
  Disposition,
  DispositionZeile,
  Frist,
  Rechenbasis,
  ReportStand,
  Takt,
} from '../typen'

/**
 * Das Dispositionsblatt - die Datei "Kladde Material ab 2026" als Ansicht.
 *
 * Aufbau wie in der Excel: je Material eine Spalte, je Kennzahl eine Zeile, von oben nach unten
 * die Rechnung vom Bestand bis zum Bestelltermin. Was die Rechnung braucht und in keinem
 * SelectLine-Report steht, wird unter der Tabelle gepflegt: geplante Produktion, Bestellungen,
 * gesperrte Ware. Diese Eingaben ändern keinen Bestand, nur seine Reichweite - deshalb ohne
 * Freigabe.
 */



function kwUndDatum(kw: string | null, tag: string | null): string {
  if (!kw || !tag) return ''
  // Die Rechnung kappt bei zehn Jahren - „KW 35/36" ist dann keine Aussage, sondern die Kappung.
  if (new Date(tag).getTime() - Date.now() > 9.5 * 365.25 * 86_400_000) return 'über 10 Jahre'
  return `${kw} (${datum(tag)})`
}

/** KW-Text zu einem ISO-Datum, wie das Backend ihn schreibt: „KW 49/26". */
function kalenderwocheText(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso + 'T00:00:00')
  const ziel = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const tag = ziel.getUTCDay() || 7
  ziel.setUTCDate(ziel.getUTCDate() + 4 - tag)
  const jahresanfang = new Date(Date.UTC(ziel.getUTCFullYear(), 0, 1))
  const woche = Math.ceil(((ziel.getTime() - jahresanfang.getTime()) / 86400000 + 1) / 7)
  return `KW ${woche}/${String(ziel.getUTCFullYear()).slice(2)}`
}

/** Die Frage an den Assistenten zu einem Hinweis der Disposition - einmal formuliert, für Einzel- und Sammel-Links. */
function klaerFrage(z: { artikelnummer: string; bezeichnung: string }, text: string, gruppe: string): string {
  return `Was muss ich bei ${z.artikelnummer} ${z.bezeichnung} (Blatt ${gruppe}) klären? Hinweis der Disposition: ${text} Bitte nachsehen, was fehlt, und einen Vorschlag machen.`
}

/**
 * Wie viel von der Tabelle im Rahmen zu sehen ist: liegen rechts noch Spalten (dann ein Schatten am
 * Rand), und wie viele Materialspalten sind gerade ganz sichtbar („12 Materialien, 5 sichtbar“).
 * Gemessen an den Köpfen der dritten Kopfzeile; die feste erste Spalte zählt nicht mit.
 */
function useSichtbareSpalten(rahmen: RefObject<HTMLDivElement | null>, abhaengig: unknown) {
  const [stand, setStand] = useState({ breiter: false, rechtsMehr: false, sichtbar: 0 })
  useEffect(() => {
    const el = rahmen.current
    if (!el) return
    let angefordert = 0
    const messen = () => {
      angefordert = 0
      const breiter = el.scrollWidth > el.clientWidth + 1
      const rechtsMehr = breiter && el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      const erste = el.querySelector<HTMLElement>('thead .tag-spalte')
      const links = el.scrollLeft + (erste?.offsetWidth ?? 0)
      const rechts = el.scrollLeft + el.clientWidth
      let sichtbar = 0
      for (const th of el.querySelectorAll<HTMLElement>('thead th.h3')) {
        if (th.offsetLeft >= links - 1 && th.offsetLeft + th.offsetWidth <= rechts + 1) sichtbar += 1
      }
      setStand((alt) => (alt.breiter === breiter && alt.rechtsMehr === rechtsMehr && alt.sichtbar === sichtbar ? alt : { breiter, rechtsMehr, sichtbar }))
    }
    const anstossen = () => {
      if (!angefordert) angefordert = requestAnimationFrame(messen)
    }
    messen()
    el.addEventListener('scroll', anstossen, { passive: true })
    const beobachter = new ResizeObserver(anstossen)
    beobachter.observe(el)
    return () => {
      el.removeEventListener('scroll', anstossen)
      beobachter.disconnect()
      if (angefordert) cancelAnimationFrame(angefordert)
    }
    // Neu messen, wenn ein anderes Blatt gerendert wurde.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rahmen, abhaengig])
  return stand
}

/**
 * Die drei Kopfzeilen stehen beim Scrollen der Seite fest, wie „Fenster fixieren" in der Excel. Der
 * Rahmen scrollt nur waagerecht (ganze-hoehe), ein sticky Kopf hinge an ihm und griffe nie - deshalb
 * schiebt der Kopf sich um das Stück nach unten, das der Rahmen oben aus dem Fenster gerollt ist.
 */
function useKopfMitziehen(rahmen: RefObject<HTMLDivElement | null>, abhaengig: unknown) {
  useEffect(() => {
    const el = rahmen.current
    const kopf = el?.querySelector('thead')
    if (!el || !kopf) return
    let angefordert = 0
    const setzen = () => {
      angefordert = 0
      const r = el.getBoundingClientRect()
      // Unten bleibt eine Zeile frei, damit der Kopf nicht über den Rahmen hinaus wandert.
      const versatz = Math.max(0, Math.min(-r.top, r.height - kopf.offsetHeight - 30))
      el.style.setProperty('--kopf-versatz', `${Math.round(versatz)}px`)
    }
    const anstossen = () => {
      if (!angefordert) angefordert = requestAnimationFrame(setzen)
    }
    setzen()
    window.addEventListener('scroll', anstossen, { passive: true })
    window.addEventListener('resize', anstossen)
    return () => {
      window.removeEventListener('scroll', anstossen)
      window.removeEventListener('resize', anstossen)
      if (angefordert) cancelAnimationFrame(angefordert)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rahmen, abhaengig])
}

const REICHWEITE_RECHNET = 'Die Reichweite rechnet nicht mit einem Mittel, sondern zieht den Bestand Monat für Monat mit dem Wert des jeweiligen Monats ab.'

/** „Okt–Dez" für die drei Monate ab dem Stichtag - die Spanne, über die die Zeile mittelt. */
function monatsspanne(stichtag: string, monate: number): string {
  const anfang = Number(stichtag.slice(5, 7)) - 1
  if (Number.isNaN(anfang)) return ''
  return `${MONATE_KURZ[anfang]}–${MONATE_KURZ[(anfang + monate - 1) % 12]}`
}

export default function DispositionSeite() {
  // Die Fällig-Liste und der Assistent verlinken ein Blatt: /disposition?gruppe=OTH.-Würfel
  const [suchparameter, setSuchparameter] = useSearchParams()
  // Ein Link mit Gruppe gewinnt; sonst das Blatt, auf dem man vor dem Reiterwechsel stand.
  const [gruppe, setGruppe] = useGemerkt('disposition.gruppe', '', suchparameter.get('gruppe'))
  // Die URL folgt der Gruppenwahl, damit ein Link auf das Blatt zeigt, das man sieht.
  useEffect(() => {
    if (gruppe && suchparameter.get('gruppe') !== gruppe) setSuchparameter({ gruppe }, { replace: true })
  }, [gruppe, setSuchparameter, suchparameter])
  // Standardansicht wie Frau Barth (Runde 4, 02.11.2026): Bestand vom letzten Dienstag, Horizont bis Freitag der
  // Folgewoche - für `heute` bleibt es bei heute/14 Tagen. Beides lässt sich hier frei überschreiben.
  const [takt, setTakt] = useGemerkt<Takt>('disposition.takt', 'dienstag_freitag')
  const [stichtag, setStichtag] = useGemerkt('disposition.stichtag', letzterDienstagIso)
  const [horizont, setHorizont] = useGemerkt('disposition.horizont', () =>
    tageZwischen(letzterDienstagIso(), freitagFolgewocheIso(letzterDienstagIso())),
  )
  function taktUebernehmen(neu: Takt) {
    setTakt(neu)
    if (neu === 'dienstag_freitag') {
      const neuerStichtag = letzterDienstagIso()
      setStichtag(neuerStichtag)
      setHorizont(tageZwischen(neuerStichtag, freitagFolgewocheIso(neuerStichtag)))
    } else {
      setStichtag(heuteIso())
      setHorizont(14)
    }
  }
  // Die Rechenbasis dieser Ansicht; leer = die Einstellung für alle (`GET /api/disposition/rechenbasis`, 02.11.2026).
  // Neuer Schlüssel: unter dem alten stand „frueher" fest gemerkt und hätte die Einstellung überdeckt.
  const [rechenbasis, setRechenbasis] = useGemerkt<Rechenbasis | ''>('disposition.rechenbasis_ansicht', '')
  const { daten: standardGeladen, neuLaden: standardLaden } = useLaden(() => ohne404(api.rechenbasisStandard()), [])
  const standard = standardGeladen === NICHT_VORHANDEN ? null : standardGeladen
  // Wie alt die Kladde ist: die letzte gebuchte Ergebnismenge. Ohne Report rechnet das Blatt mit altem Bestand.
  const { daten: reports } = useLaden<ReportStand[]>(() => api.importeLetzte(), [])
  const ergebnismenge = (reports ?? []).find((r) => r.dokumenttyp === 'ergebnismenge')
  const ergebnismengeFehlt = reports !== null && (ergebnismenge === undefined || ergebnismenge.tage_her === null || !ergebnismenge.gebucht_am)
  const ergebnismengeAlt = ergebnismengeFehlt || (ergebnismenge?.tage_her ?? 0) > 3
  // Die Hinweise sind eingeklappt, bis man sie sehen will - die Tabelle ist das Blatt, nicht die Meldung.
  const [hinweiseOffen, setHinweiseOffen] = useState(false)
  const rahmen = useRef<HTMLDivElement | null>(null)
  const { fehler, arbeitet, setFehler, ausfuehren } = useAktion()

  // Formulare - bewusst einfach: ein Satz Felder je Eingabeart, kein Dialog.
  const [planTag, setPlanTag] = useState(heuteIso())
  const [planArtikel, setPlanArtikel] = useState('')
  const [planMenge, setPlanMenge] = useState('')
  const [bestMaterial, setBestMaterial] = useState('')
  const [bestMenge, setBestMenge] = useState('')
  const [bestEinheit, setBestEinheit] = useState<'t' | 'stk'>('t')
  const [bestStatus, setBestStatus] = useState<Bestellstatus>('beauftragt')
  const [bestTag, setBestTag] = useState('')
  const [bestBeleg, setBestBeleg] = useState('')
  const [sperrMaterial, setSperrMaterial] = useState('')
  const [sperrMenge, setSperrMenge] = useState('')
  const [sperrGrund, setSperrGrund] = useState('')
  const [fristMaterial, setFristMaterial] = useState('')
  const [fristBis, setFristBis] = useState('')
  const [fristGrund, setFristGrund] = useState('grüner Punkt (PPWR)')
  const [fristVorlauf, setFristVorlauf] = useState('')
  const [fristUngeklaert, setFristUngeklaert] = useState(false)

  const { daten: gruppen, fehler: gruppenFehler } = useLaden(() => api.gruppen(), [], {
    onGeladen: (werte) => {
      // Eine gemerkte Gruppe, die es nicht mehr gibt, fällt auf die erste zurück.
      if (werte.length > 0) setGruppe((alt) => (alt && werte.includes(alt) ? alt : werte[0]))
    },
  })
  const { daten: fertigartikel, fehler: artikelFehler } = useLaden<Artikel[]>(
    () => api.artikel().then(aktiveFertigartikel),
    [],
  )

  const blattAbhaengig = [gruppe, stichtag, horizont, rechenbasis]
  const {
    daten: blatt,
    laedt,
    fehler: blattFehler,
    neuLaden: blattLaden,
  } = useLaden<Disposition>(() => api.disposition(gruppe, stichtag, horizont, rechenbasis || undefined), blattAbhaengig, {
    wenn: Boolean(gruppe),
    onGeladen: (wert) => {
      if (wert.zeilen.length > 0) {
        // Die Formulare zeigen nur Material dieses Blatts - nach einem Gruppenwechsel darf
        // keine Auswahl aus dem alten Blatt stehen bleiben und still dorthin buchen.
        const imBlatt = (alt: string) =>
          wert.zeilen.some((z) => z.artikelnummer === alt) ? alt : wert.zeilen[0].artikelnummer
        setBestMaterial(imBlatt)
        setSperrMaterial(imBlatt)
        setFristMaterial(imBlatt)
      }
    },
  })
  // Die Fristen gelten über alle Blätter; sie laden im Takt des Blatts, damit ein Wechsel sie mitzieht.
  const { daten: fristenDaten, neuLaden: fristenLaden } = useLaden<Frist[]>(() => api.fristen(), blattAbhaengig, {
    wenn: Boolean(gruppe),
  })
  const fristen = fristenDaten ?? []
  const ladeFehler = gruppenFehler ?? artikelFehler ?? blattFehler

  const laden = useCallback(() => {
    blattLaden()
    fristenLaden()
  }, [blattLaden, fristenLaden])

  /** Eine Aktion ausführen und danach das Blatt neu laden - jede Eingabe ändert eine Reichweite. */
  const tun = (aktion: () => Promise<unknown>) =>
    ausfuehren(async () => {
      await aktion()
      laden()
    })

  /** Enter im kleinen Formular speichert, statt die Eingabe verfallen zu lassen (Befund 19.09.2026:
   * Menge tippen, Enter drücken, nichts passierte - die Karte ist kein Formular). */
  function beiEnter(speichern: () => void) {
    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        speichern()
      }
    }
  }

  function planSpeichern() {
    const menge = zahlLesen(planMenge)
    if (!planArtikel || menge === null) {
      setFehler('Fertigartikel und Menge angeben.')
      return
    }
    void tun(async () => {
      await api.planSetzen({ tag: planTag, fertigartikel: planArtikel, menge_t: menge })
      setPlanMenge('')
    })
  }

  function bestellungSpeichern() {
    const menge = zahlLesen(bestMenge)
    if (!bestMaterial || menge === null) {
      setFehler('Material und Menge angeben.')
      return
    }
    void tun(async () => {
      await api.bestellungAnlegen({
        material: bestMaterial,
        menge_t: bestEinheit === 't' ? menge : null,
        menge_original: bestEinheit === 'stk' ? menge : null,
        status: bestStatus,
        liefer_tag: bestTag || null,
        beleg: bestBeleg || null,
      })
      setBestMenge('')
      setBestBeleg('')
    })
  }

  function statusAendern(bestellung: Bestellung, status: Bestellstatus) {
    void tun(() =>
      api.bestellungAendern(bestellung.id, {
        material: bestellung.material,
        menge_t: bestellung.menge_t,
        menge_original: bestellung.menge_original,
        einheit_original: bestellung.einheit_original,
        status,
        liefer_tag: bestellung.liefer_tag,
        beleg: bestellung.beleg,
        bemerkung: bestellung.bemerkung,
      }),
    )
  }

  function sperrungSpeichern() {
    const menge = zahlLesen(sperrMenge)
    if (!sperrMaterial || menge === null) {
      setFehler('Material und Menge angeben.')
      return
    }
    void tun(async () => {
      await api.sperrungAnlegen({ material: sperrMaterial, menge_t: menge, grund: sperrGrund || null })
      setSperrMenge('')
      setSperrGrund('')
    })
  }

  function fristSpeichern() {
    if (!fristMaterial || !fristBis || !fristGrund.trim()) {
      setFehler('Material, Datum und Grund angeben.')
      return
    }
    void tun(async () => {
      const vorlauf = fristVorlauf.trim() ? tageLesen(fristVorlauf) : null
      if (fristVorlauf.trim() && vorlauf === null) throw new Error('Vorlauf als Wochen oder Tage angeben, z.B. „4 Wo“.')
      await api.fristAnlegen({
        material: fristMaterial,
        verwendbar_bis: fristBis,
        grund: fristGrund,
        vorlauf_tage: vorlauf,
        // Nur mitschicken, wenn ungeklärt - ein älteres Backend kennt das Feld nicht.
        ...(fristUngeklaert ? { bestaetigt: false } : {}),
      })
      setFristBis('')
      setFristVorlauf('')
      setFristUngeklaert(false)
    })
  }

  /** Frist als geklärt oder ungeklärt festhalten (Frau Barth 27.10.2026: „noch in Arbeit und ungeklärt"). */
  function fristKlaeren(f: Frist, bestaetigt: boolean) {
    let bemerkung: string | null = null
    if (!bestaetigt) {
      const antwort = window.prompt(`Frist ${f.material} als ungeklärt vermerken. Was ist offen? (optional)`, f.bemerkung ?? '')
      if (antwort === null) return
      bemerkung = antwort.trim() || null
    }
    void tun(() => api.fristKlaerung(f.id, bestaetigt, bemerkung))
  }

  /** Die Auswahl dieser Ansicht zur Rechenbasis für alle machen - danach folgt die Ansicht wieder der Einstellung. */
  function alsStandard() {
    if (!rechenbasis) return
    if (!window.confirm(`„${rechenbasisTitel(rechenbasis, standard)}“ als Rechenbasis für alle setzen? Sie gilt dann für Dispositionsblatt, Heute, Fällig-Liste, Excel-Export und Assistent.`)) return
    void ausfuehren(async () => {
      await api.rechenbasisStandardSetzen(rechenbasis)
      setRechenbasis('')
      standardLaden()
    })
  }

  const zeilen = useMemo(() => blatt?.zeilen ?? [], [blatt])
  // Beim Neuladen bleibt das alte Blatt stehen (nur blasser), damit nach einer Eingabe weder die Seite
  // springt noch der Rahmen nach links zurückrollt - gemessen wird, sobald ein Blatt da ist.
  const spalten = useSichtbareSpalten(rahmen, blatt)
  useKopfMitziehen(rahmen, blatt)
  useScrollMerken('disposition.scroll', rahmen, Boolean(blatt) && !laedt)
  const vermerkArtikel = useMemo(
    () => zeilen.map((z) => ({ artikelnummer: z.artikelnummer, bezeichnung: z.bezeichnung })),
    [zeilen],
  )
  const fristenImBlatt = fristen.filter((f) => zeilen.some((z) => z.artikelnummer === f.material))
  const bestellungen = zeilen.flatMap((z) => z.bestellungen)
  const sperrungen = zeilen.flatMap((z) => z.sperrungen)
  const handlungen = zeilen.filter((z) => z.handlung)
  // Je Blatt ein Satz; eine Zeile mit eigenem, anderem Satz (anderer Stand) kommt dazu - gleiche nur einmal.
  const datenstandHinweise = [
    ...new Set([blatt?.datenstand_hinweis, ...zeilen.map((z) => z.datenstand_hinweis)].filter((h): h is string => Boolean(h))),
  ]
  const mitTrend = zeilen.some((z) => z.trend_hinweis)
  // Die Zeile „Datenstand" nur, wenn ein Material einen anderen Stand hat als das Blatt - sonst stünde oben derselbe Satz.
  const mitDatenstand = zeilen.some((z) => z.datenstand_hinweis && z.datenstand_hinweis !== blatt?.datenstand_hinweis)
  // Gleicher Hinweis bei mehreren Materialien: eine Zeile, nicht sieben - und ein Klick für alle.
  const hinweisgruppen = new Map<string, typeof zeilen>()
  for (const z of handlungen) {
    const liste = hinweisgruppen.get(z.handlung ?? '') ?? []
    liste.push(z)
    hinweisgruppen.set(z.handlung ?? '', liste)
  }

  // Eine Kennzahl je Zeile; die Excel liest sich genauso von oben nach unten.
  const kennzahlen: {
    titel: string
    wert: (z: DispositionZeile) => string
    titelHinweis?: (z: DispositionZeile) => string | undefined
    klasse?: (z: DispositionZeile) => string
    summe?: boolean
  }[] = [
    {
      titel: 'Bestand',
      wert: (z) => tonnen(z.bestand_t),
      titelHinweis: (z) =>
        `${z.bestand_am ? `Stand ${datum(z.bestand_am)}` : 'noch nichts gebucht'}${z.produktion_gebucht_bis ? ` · Produktion gebucht bis ${datum(z.produktion_gebucht_bis)}` : ''}`,
    },
    { titel: 'gesperrt / Reklamation', wert: (z) => (z.gesperrt_t ? `- ${tonnen(z.gesperrt_t)}` : '') },
    {
      titel: `geplante Produktion bis ${blatt ? datum(blatt.horizont_bis) : ''}`,
      wert: (z) => (z.geplant_t ? `- ${tonnen(z.geplant_t)}` : ''),
    },
    {
      titel: `Lieferungen bis ${blatt ? datum(blatt.horizont_bis) : ''}`,
      wert: (z) => (z.lieferungen_t ? `+ ${tonnen(z.lieferungen_t)}` : ''),
    },
    {
      titel: 'Zwischensumme',
      wert: (z) => tonnen(z.zwischensumme_t),
      klasse: (z) => (z.knapp || z.zwischensumme_t <= 0 ? 'unter-mindestbestand' : ''),
      summe: true,
    },
    { titel: 'Mindestbestand', wert: (z) => (z.mindestbestand_t !== null ? tonnen(z.mindestbestand_t) : '') },
    // Zwei Mittel, ehrlich beschriftet: „Verbrauch je Monat" allein las sich als die Zahl, mit der die
    // Reichweite rechnet (Frau Barth, 20.10.2026). Die rechnet aber Monat für Monat mit der Kurve.
    {
      titel: `Ø Verbrauch je Monat ${blatt ? monatsspanne(blatt.stichtag, 3) : ''}`,
      wert: (z) => (z.verbrauch_je_monat_t !== null ? tonnen(z.verbrauch_je_monat_t) : 'unbekannt'),
      titelHinweis: (z) => `Mittel der nächsten drei Monate · ${z.verbrauch_quelle}. ${REICHWEITE_RECHNET}`,
    },
    {
      titel: 'Ø Verbrauch je Monat, 12 Monate',
      wert: (z) => (z.verbrauch_12_monate_t != null ? tonnen(z.verbrauch_12_monate_t) : ''),
      titelHinweis: (z) =>
        `Mittel der nächsten zwölf Monate${blatt ? ` (${monatsspanne(blatt.stichtag, 12)})` : ''} · ${z.verbrauch_quelle}. ${REICHWEITE_RECHNET}`,
    },
    {
      titel: 'reicht bis (Ist-Verbrauch)',
      // Mit Trend: das laufende Jahr liegt weit über dem Vorjahr, die Kurve rechnet mit seinem Satz (02.11.2026).
      wert: (z) => `${kwUndDatum(z.reicht_bis_ist_kw, z.reicht_bis_ist)}${z.trend_hinweis ? ' · mit Trend' : ''}`,
      titelHinweis: (z) => z.trend_hinweis ?? undefined,
      klasse: (z) => (z.reicht_bis_ist && z.reicht_bis === z.reicht_bis_ist ? 'massgeblich' : 'leise'),
    },
    {
      titel: 'reicht bis (Forecast GF)',
      wert: (z) => kwUndDatum(z.reicht_bis_forecast_kw, z.reicht_bis_forecast),
      klasse: (z) => (z.reicht_bis_forecast && z.reicht_bis === z.reicht_bis_forecast ? 'massgeblich' : 'leise'),
    },
    { titel: 'reicht bis (maßgeblich)', wert: (z) => kwUndDatum(z.reicht_kw, z.reicht_bis), summe: true },
    {
      titel: 'Frist (verwendbar bis)',
      wert: (z) =>
        z.frist_bis
          ? `${z.frist_bestaetigt === false ? 'Frist ungeklärt: ' : ''}${kwUndDatum(kalenderwocheText(z.frist_bis), z.frist_bis)} · ${z.frist_grund ?? ''}${z.frist_vorlauf_tage ? ` (Frist ${datum(z.frist_ende)}, Produktion ${wochen(z.frist_vorlauf_tage)} vorher beenden)` : ''}`
          : '',
      titelHinweis: (z) => (z.frist_bestaetigt === false ? 'Die Frist ist noch nicht geklärt - gerechnet wird trotzdem mit ihr. Klären unten unter „Verwendbar bis".' : undefined),
      klasse: (z) => (z.frist_bis && z.reicht_bis && z.frist_bis < z.reicht_bis ? 'unter-mindestbestand' : 'leise'),
    },
    {
      titel: 'Abruf bis',
      wert: (z) => kwUndDatum(z.abruf_kw, z.abruf_bis),
      titelHinweis: (z) => `Vorlauf ${z.abruf_vorlauf_tage} Tage + Puffer ${z.abruf_puffer_tage} Tage`,
      klasse: (z) => (z.abruf_bis && blatt && z.abruf_bis <= blatt.stichtag ? 'unter-mindestbestand' : ''),
    },
    { titel: 'beim Hersteller', wert: (z) => (z.beim_hersteller_t ? `+ ${tonnen(z.beim_hersteller_t)}` : '') },
    { titel: 'in Produktion', wert: (z) => (z.in_produktion_t ? `+ ${tonnen(z.in_produktion_t)}` : '') },
    { titel: 'beauftragt, später', wert: (z) => (z.beauftragt_t ? `+ ${tonnen(z.beauftragt_t)}` : '') },
    { titel: 'Bestellung geplant', wert: (z) => (z.geplant_bestellt_t ? `+ ${tonnen(z.geplant_bestellt_t)}` : '') },
    { titel: 'Gesamtbestand', wert: (z) => tonnen(z.gesamtbestand_t), summe: true },
    { titel: 'reicht bis (gesamt)', wert: (z) => kwUndDatum(z.gesamt_reicht_kw, z.gesamt_reicht_bis) },
    {
      titel: 'Produktionszeit · Puffer',
      wert: (z) =>
        `${z.lieferzeit_tage !== null ? wochen(z.lieferzeit_tage) : 'fehlt'} · ${wochen(z.puffer_tage)}${
          z.layout_vorlauf_tage ? ` · Layout ${wochen(z.layout_vorlauf_tage)}` : ''
        }`,
      titelHinweis: (z) =>
        `${z.lieferzeit_tage ?? '?'} Tage Produktionszeit · ${z.puffer_tage} Tage Puffer${z.layout_vorlauf_tage ? ` · ${z.layout_vorlauf_tage} Tage Layout` : ''}`,
    },
    {
      titel: 'Nachauflage bestellen bis',
      wert: (z) => kwUndDatum(z.bestellen_kw, z.bestellen_bis),
      klasse: (z) =>
        z.bestellen_bis && blatt && z.bestellen_bis <= blatt.stichtag ? 'unter-mindestbestand' : '',
    },
    // Nur wenn ein Material des Blatts Vorstufen hat (Verbund: Alu, Pergament) - sonst eine leere Zeile.
    ...(zeilen.some((z) => z.vorstufen_tage)
      ? [
          {
            titel: 'Vorstufen bestellen bis',
            wert: (z: DispositionZeile) => kwUndDatum(z.vorstufen_bestellen_kw, z.vorstufen_bestellen_bis),
            titelHinweis: (z: DispositionZeile) =>
              z.vorstufen_tage ? `${z.vorstufen ?? 'Vorstufen'}: ${wochen(z.vorstufen_tage)} vor der Nachauflage` : undefined,
            klasse: (z: DispositionZeile) =>
              z.vorstufen_bestellen_bis && blatt && z.vorstufen_bestellen_bis <= blatt.stichtag ? 'unter-mindestbestand' : '',
          },
        ]
      : []),
    {
      titel: 'Bestand in Paletten',
      wert: (z) => (z.paletten_bestand !== null ? `${menge(z.paletten_bestand)} Pal.` : ''),
      titelHinweis: (z) => (z.stueck_je_palette ? `${stueck(z.stueck_je_palette)} Stück je Palette` : 'keine Palettengröße hinterlegt'),
    },
  ]

  return (
    <>
      <div className="leiste">
        <label>
          Gruppe{' '}
          <select value={gruppe} onChange={(e) => setGruppe(e.target.value)}>
            {(gruppen ?? []).length === 0 && <option value="">keine vorhanden</option>}
            {(gruppen ?? []).map((wert) => (
              <option key={wert} value={wert}>
                {wert}
              </option>
            ))}
          </select>
        </label>
        <label title="Wie Stichtag und Horizont ohne eigene Angabe gelten würden (Runde 4, 02.11.2026). Hier übernimmt die Wahl sofort Stichtag und Horizont dieser Ansicht; die Vorgabe für alle steht unter Einstellungen.">
          Takt{' '}
          <select value={takt} onChange={(e) => taktUebernehmen(e.target.value as Takt)}>
            {TAKTE.map((t) => (
              <option key={t.wert} value={t.wert}>
                {t.wert === 'dienstag_freitag' ? 'wie Frau Barth' : t.titel}
              </option>
            ))}
          </select>
        </label>
        <label>
          Stichtag <input type="date" value={stichtag} onChange={(e) => setStichtag(e.target.value)} />
        </label>
        <label>
          Horizont{' '}
          <select value={horizont} onChange={(e) => setHorizont(Number(e.target.value))}>
            <option value={7}>1 Woche</option>
            <option value={14}>2 Wochen</option>
            <option value={21}>3 Wochen</option>
            <option value={28}>4 Wochen</option>
            {![7, 14, 21, 28].includes(horizont) && (
              <option value={horizont}>bis {datum(plusTage(stichtag, horizont))}</option>
            )}
          </select>
        </label>
        <label title="Womit die maßgebliche Reichweite in dieser Ansicht gerechnet wird, wenn Ist-Verbrauch und Forecast auseinanderliegen. Die Rechenbasis für alle steht unter Einstellungen.">
          Rechenbasis{' '}
          <select value={rechenbasis} onChange={(e) => setRechenbasis(e.target.value as Rechenbasis | '')}>
            <option value="">
              wie eingestellt{standard ? `: ${rechenbasisTitel(standard.rechenbasis, standard)}` : ''}
            </option>
            {RECHENBASEN.map((r) => (
              <option key={r.wert} value={r.wert}>
                {r.titel}
              </option>
            ))}
          </select>
        </label>
        {rechenbasis && standard && rechenbasis !== standard.rechenbasis && (
          <span className="hinweis">
            nur diese Ansicht -{' '}
            <button className="leise" disabled={arbeitet} onClick={alsStandard}>
              für alle übernehmen
            </button>{' '}
            <Link to="/einstellungen#rechenbasis">Einstellung</Link>
          </span>
        )}
        <a className="knopf" href={api.exportDisposition(gruppe, stichtag, rechenbasis || undefined)} download>
          Als Excel (Kladde Material)
        </a>
        {spalten.breiter && zeilen.length > 0 && (
          <span className="hinweis spalten-zaehler" title="Die Tabelle ist breiter als das Fenster - im Rahmen nach rechts scrollen">
            {zeilen.length} Materialien, {spalten.sichtbar} sichtbar
          </span>
        )}
        <span
          className="hinweis"
          title="Geplante Produktion und Lieferungen zählen bis zum Ende des Horizonts, alles Spätere steht im Gesamtbestand. Zwei Reichweiten: Ist-Verbrauch und Forecast des Chefs - die frühere ist maßgeblich."
        >
          Alle Mengen in Tonnen Fertigprodukt.
        </span>
      </div>

      {/* Eine Zeile zum Alter der Kladde: ohne aktuelle Ergebnismenge rechnet das Blatt mit altem Bestand. */}
      {reports !== null && (
        <p className={`stand-zeile${ergebnismengeAlt ? ' alt' : ''}`}>
          {ergebnismengeFehlt ? (
            <>
              Letzte Ergebnismenge: fehlt - PDF an othuena@intsode.com schicken oder unter{' '}
              <Link to="/importe">Importe</Link> hochladen.
            </>
          ) : (
            <>
              Letzte Ergebnismenge {datum((ergebnismenge?.gebucht_am ?? '').slice(0, 10))} ({ergebnismenge?.tage_her} Tage her)
              {blatt?.produktion_gebucht_bis && <> · Produktion gebucht bis {datum(blatt.produktion_gebucht_bis)}</>}
              {ergebnismengeAlt && (
                <>
                  {' '}- neues PDF an othuena@intsode.com schicken oder unter <Link to="/importe">Importe</Link> hochladen.
                </>
              )}
            </>
          )}
        </p>
      )}

      <Meldungen fehler={ladeFehler ?? fehler} />
      {/* Datenstand (02.11.2026): ist die gebuchte Produktion älter als eine Woche, stehen Bestand und Reichweiten
          auf diesem Stand - das gehört über das Blatt, nicht in einen Tooltip. */}
      {blatt && datenstandHinweise.length > 0 && (
        <Meldung art="warnung" titel="Datenstand">
          {datenstandHinweise.length === 1 ? (
            datenstandHinweise[0]
          ) : (
            <ul>
              {datenstandHinweise.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          )}
        </Meldung>
      )}
      {gruppe && <Beobachtungen gruppe={gruppe} stichtag={stichtag || undefined} />}
      {laedt && !blatt && <p className="hinweis">Wird geladen.</p>}

      {blatt && handlungen.length > 0 && (
        <div className="meldung warnung aufmerksamkeit">
          <div className="leiste">
            <b>
              {handlungen.length} {handlungen.length === 1 ? 'Material braucht' : 'Materialien brauchen'} Aufmerksamkeit
            </b>
            <button className="leise" onClick={() => setHinweiseOffen((o) => !o)}>
              {hinweiseOffen ? 'ausblenden' : 'anzeigen'}
            </button>
          </div>
          {hinweiseOffen && (
            <>
              <ul>
                {[...hinweisgruppen.entries()].map(([text, liste]) =>
                  liste.length === 1 ? (
                    <li key={text}>
                      <Link
                        className="hinweis-link"
                        title="Im Assistenten klären: was genau fehlt und was zu tun ist"
                        to={assistentPfad(klaerFrage(liste[0], text, gruppe))}
                      >
                        {liste[0].artikelnummer} {liste[0].bezeichnung}
                      </Link>
                      : {text}
                    </li>
                  ) : (
                    <li key={text}>
                      {text}{' '}
                      {liste.map((z, i) => (
                        <span key={z.artikelnummer}>
                          {i > 0 && ', '}
                          <Link
                            className="hinweis-link"
                            title="Im Assistenten klären: was genau fehlt und was zu tun ist"
                            to={assistentPfad(klaerFrage(z, text, gruppe))}
                          >
                            {z.artikelnummer} {z.bezeichnung}
                          </Link>
                        </span>
                      ))}
                      {' '}
                      <Link
                        className="hinweis-link"
                        style={{ fontWeight: 600 }}
                        to={assistentPfad(
                          `Auf dem Blatt ${gruppe} haben ${liste.length} Materialien denselben Hinweis „${text}“: ${liste
                            .map((z) => `${z.artikelnummer} ${z.bezeichnung}`)
                            .join(', ')}. Bitte nachsehen, was dahintersteckt, und mir einen Weg vorschlagen, es für alle auf einmal zu lösen - Punkt für Punkt, mit Rückfrage vor jeder Änderung.`,
                        )}
                      >
                        alle {liste.length} zusammen klären
                      </Link>
                    </li>
                  ),
                )}
              </ul>
              <div className="hinweis">Ein Klick auf ein Material öffnet den Assistenten, der klärt, was fehlt.</div>
            </>
          )}
        </div>
      )}

      {blatt && zeilen.length === 0 && (
        <p className="leer">Für diese Gruppe sind keine Materialien hinterlegt.</p>
      )}

      {blatt && zeilen.length > 0 && (
        <div className={`tabellen-huelle${spalten.rechtsMehr ? ' rechts-mehr' : ''}${laedt ? ' veraltet' : ''}`} aria-busy={laedt}>
        <div className="tabellenrahmen ganze-hoehe kopf-mitziehen" ref={rahmen}>
          <table>
            <thead>
              <tr>
                <th className="tag-spalte h1" rowSpan={3}>
                  Stichtag {datum(blatt.stichtag)}
                </th>
                {bloecke(zeilen, (z) => z.block).map((block, index) => (
                  <th key={`${block.titel}-${index}`} className="h1 block" colSpan={block.breite}>
                    {block.titel}
                  </th>
                ))}
              </tr>
              <tr>
                {zeilen.map((z) => (
                  <th key={z.artikelnummer} className="h2 zahl">
                    <div className="kopf-eng">{z.bezeichnung}</div>
                  </th>
                ))}
              </tr>
              <tr>
                {zeilen.map((z) => (
                  <th key={z.artikelnummer} className="h3 zahl">
                    {z.artikelnummer}
                    {(z.lieferant ?? z.hersteller) && <span className="hinweis"> · {z.lieferant ?? z.hersteller}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kennzahlen.map((k) => (
                <tr key={k.titel}>
                  <td className={`tag-spalte ${k.summe ? 'gruppe-summe' : ''}`}>{k.titel}</td>
                  {zeilen.map((z) => (
                    <td
                      key={z.artikelnummer}
                      className={`zahl ${k.summe ? 'gruppe-summe' : ''} ${k.klasse ? k.klasse(z) : ''}`}
                      title={k.titelHinweis ? k.titelHinweis(z) : undefined}
                    >
                      {k.wert(z)}
                    </td>
                  ))}
                </tr>
              ))}
              {mitTrend && (
                <tr>
                  <td className="tag-spalte" title="Das laufende Jahr liegt weit über dem Vorjahr - die Ist-Kurve rechnet mit dem Satz des laufenden Jahres, die Reichweite endet früher.">
                    Trend laufendes Jahr
                  </td>
                  {zeilen.map((z) => (
                    <td key={z.artikelnummer} className="hinweis">
                      {z.trend_hinweis && (
                        <div className="zelle-eng">
                          <span className="marke warnung">Trend</span> {z.trend_hinweis}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              )}
              {mitDatenstand && (
                <tr>
                  <td className="tag-spalte">Datenstand</td>
                  {zeilen.map((z) => (
                    <td key={z.artikelnummer} className="hinweis">
                      {z.datenstand_hinweis && (
                        <div className="zelle-eng">
                          <span className="marke warnung">alt</span> {z.datenstand_hinweis}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              )}
              <tr>
                <td className="tag-spalte">Hinweis</td>
                {zeilen.map((z) => (
                  <td key={z.artikelnummer} className="hinweis">
                    <div className="zelle-eng">{z.handlung ?? ''}</div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="tag-spalte">Vermerke</td>
                {zeilen.map((z) => (
                  <td key={z.artikelnummer} className={z.vermerke.length > 0 ? 'vermerk-zelle' : ''}>
                    <div className="zelle-eng">
                    {z.vermerke.map((v) => (
                      <div key={v.id} className="hinweis">
                        <b>{v.art}</b>: {v.text}
                        {v.tag ? ` (${datum(v.tag)}${v.bis ? ` - ${datum(v.bis)}` : ''})` : ''}
                      </div>
                    ))}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      )}

      {blatt && blatt.lieferanten.length > 0 && (
        <div className="karte" style={{ marginTop: 14 }}>
          <h3>Je Lieferant</h3>
          <p className="hinweis">
            Bestellt wird über das ganze Sortiment eines Lieferanten - Mindestabruf, Staffel, Paarigkeit.
            Der Bestellvorschlag rechnet das durch.
          </p>
          <table>
            <thead>
              <tr>
                <th>Lieferant</th>
                <th>Materialien</th>
                <th>Menge je Bestellung</th>
                <th>Abruf fällig</th>
                <th>Nachauflage fällig</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {blatt.lieferanten.map((g) => (
                <tr key={g.name}>
                  <td>{g.name}{g.lieferant_id === null && <span className="hinweis"> (nicht als Lieferant angelegt)</span>}</td>
                  <td className="hinweis">{g.materialien.join(', ')}</td>
                  <td>
                    {g.mengenregel ?? '-'}
                  </td>
                  <td className={g.abruf_faellig.length > 0 ? 'unter-mindestbestand' : ''}>{g.abruf_faellig.join(', ') || '-'}</td>
                  <td className={g.nachauflage_faellig.length > 0 ? 'unter-mindestbestand' : ''}>{g.nachauflage_faellig.join(', ') || '-'}</td>
                  <td>
                    {g.lieferant_id !== null ? (
                      <Link
                        to={`/bestellen?lieferant=${encodeURIComponent(g.name)}&gruppe=${encodeURIComponent(gruppe)}`}
                        title="Bestellvorschlag für diesen Lieferanten und dieses Blatt rechnen"
                      >
                        Bestellvorschlag
                      </Link>
                    ) : (
                      <Link to={`/lieferanten?name=${encodeURIComponent(g.name)}&materialien=${encodeURIComponent(g.materialien.join(','))}`}>
                        als Lieferant anlegen
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {blatt && (
        <div className="pflege-liste">
          <ReichweiteRechnen materialien={vermerkArtikel} rechenbasis={rechenbasis} />

          <Pflegeabschnitt
            titel="Geplante Produktion"
            leer="nichts im Horizont"
            anzahl={blatt.plan.length}
            erklaerung="Aus dem Wochenplan des Verkaufs. Zieht über die Kopplung Material ab, bevor es gebucht ist."
            formular={
              <div className="leiste felder">
                <label className="feld">
                  <span className="hinweis">Tag</span>
                  <input type="date" value={planTag} onChange={(e) => setPlanTag(e.target.value)} />
                </label>
                <label className="feld">
                  <span className="hinweis">Fertigartikel</span>
                  <ArtikelAuswahl artikel={fertigartikel ?? []} wert={planArtikel} onChange={setPlanArtikel} leer="bitte wählen" />
                </label>
                <label className="feld">
                  <span className="hinweis">Menge (t)</span>
                  <input
                    type="text"
                    style={{ width: 80, textAlign: 'right' }}
                    placeholder="t"
                    value={planMenge}
                    onKeyDown={beiEnter(planSpeichern)}
                    onChange={(e) => setPlanMenge(e.target.value)}
                  />
                </label>
                <button className="haupt" disabled={arbeitet} onClick={planSpeichern}>
                  Eintragen
                </button>
              </div>
            }
          >
            <table>
              <tbody>
                {blatt.plan.map((p) => (
                  <tr key={p.id}>
                    <td>{datum(p.tag)}</td>
                    <td>
                      {p.fertigartikel} {p.bezeichnung}
                    </td>
                    <td className="zahl">{tonnen(p.menge_t)} t</td>
                    <td>
                      <button disabled={arbeitet} onClick={() => void tun(() => api.planLoeschen(p.id))}>
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Pflegeabschnitt>

          <Pflegeabschnitt
            titel="Bestellungen"
            leer="keine offenen"
            anzahl={bestellungen.length}
            erklaerung="In Produktion beim Lieferanten, dort fertig liegend, beauftragt mit Termin oder erst geplant. Geliefert heißt: der Wareneingang ist gebucht, die Bestellung zählt nicht mehr."
            formular={
              <div className="leiste felder">
                <label className="feld">
                  <span className="hinweis">Material</span>
                  <ArtikelAuswahl artikel={zeilen} wert={bestMaterial} onChange={setBestMaterial} />
                </label>
                <label className="feld">
                  <span className="hinweis">Menge</span>
                  <input
                    type="text"
                    style={{ width: 90, textAlign: 'right' }}
                    placeholder="Menge"
                    value={bestMenge}
                    onKeyDown={beiEnter(bestellungSpeichern)}
                    onChange={(e) => setBestMenge(e.target.value)}
                  />
                </label>
                <label className="feld">
                  <span className="hinweis">Einheit</span>
                  <select value={bestEinheit} onChange={(e) => setBestEinheit(e.target.value as 't' | 'stk')}>
                    <option value="t">t</option>
                    <option value="stk">Stk / m (über MVN)</option>
                  </select>
                </label>
                <label className="feld">
                  <span className="hinweis">Status</span>
                  <select value={bestStatus} onChange={(e) => setBestStatus(e.target.value as Bestellstatus)}>
                    {(Object.keys(BESTELLSTATUS) as Bestellstatus[])
                      .filter((s) => s !== 'geliefert')
                      .map((s) => (
                        <option key={s} value={s}>
                          {BESTELLSTATUS[s]}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="feld">
                  <span className="hinweis">Liefertermin</span>
                  <input type="date" value={bestTag} onChange={(e) => setBestTag(e.target.value)} />
                </label>
                <label className="feld">
                  <span className="hinweis">Beleg / AB</span>
                  <input
                    type="text"
                    style={{ width: 110 }}
                    placeholder="Beleg / AB"
                    value={bestBeleg}
                    onKeyDown={beiEnter(bestellungSpeichern)}
                    onChange={(e) => setBestBeleg(e.target.value)}
                  />
                </label>
                <button className="haupt" disabled={arbeitet} onClick={bestellungSpeichern}>
                  Anlegen
                </button>
              </div>
            }
          >
            <table>
              <tbody>
                {bestellungen.map((b) => (
                  <tr key={b.id}>
                    <td>
                      {b.material} {b.bezeichnung}
                    </td>
                    <td className="zahl">{tonnen(b.menge_t)} t</td>
                    <td>
                      <select
                        value={b.status}
                        disabled={arbeitet}
                        onChange={(e) => statusAendern(b, e.target.value as Bestellstatus)}
                      >
                        {(Object.keys(BESTELLSTATUS) as Bestellstatus[]).map((s) => (
                          <option key={s} value={s}>
                            {BESTELLSTATUS[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={b.ueberfaellig || b.nach_reichweite ? 'unter-mindestbestand' : ''}>
                      {b.liefer_kw ? `${b.liefer_kw} (${datum(b.liefer_tag)})` : ''}
                      {b.ueberfaellig && ' überfällig'}
                      {b.nach_reichweite && (
                        <span title="Der Bestand ist vorher aufgebraucht - Termin vorziehen oder Zwischenabruf klären.">
                          {' '}nach Reichweitenende
                        </span>
                      )}
                    </td>
                    <td className="hinweis">{b.beleg ?? ''}</td>
                    <td>
                      <button
                        disabled={arbeitet}
                        onClick={() => void tun(() => api.bestellungLoeschen(b.id))}
                      >
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Pflegeabschnitt>

          <Pflegeabschnitt
            titel="Gesperrte Ware / Reklamation"
            leer="nichts gesperrt"
            anzahl={sperrungen.length}
            erklaerung="Steht im Bestand, darf aber nicht verplant werden. Aufheben, sobald die Ware wieder frei ist."
            formular={
              <div className="leiste felder">
                <label className="feld">
                  <span className="hinweis">Material</span>
                  <ArtikelAuswahl artikel={zeilen} wert={sperrMaterial} onChange={setSperrMaterial} />
                </label>
                <label className="feld">
                  <span className="hinweis">Menge (t)</span>
                  <input
                    type="text"
                    style={{ width: 80, textAlign: 'right' }}
                    placeholder="t"
                    value={sperrMenge}
                    onKeyDown={beiEnter(sperrungSpeichern)}
                    onChange={(e) => setSperrMenge(e.target.value)}
                  />
                </label>
                <label className="feld">
                  <span className="hinweis">Grund</span>
                  <input
                    type="text"
                    style={{ width: 200 }}
                    placeholder="z.B. Reklamation Druckbild"
                    value={sperrGrund}
                    onKeyDown={beiEnter(sperrungSpeichern)}
                    onChange={(e) => setSperrGrund(e.target.value)}
                  />
                </label>
                <button className="haupt" disabled={arbeitet} onClick={sperrungSpeichern}>
                  Sperren
                </button>
              </div>
            }
          >
            <table>
              <tbody>
                {sperrungen.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.material} {s.bezeichnung}
                    </td>
                    <td className="zahl">{tonnen(s.menge_t)} t</td>
                    <td className="hinweis">
                      seit {datum(s.seit)}
                      {s.grund ? ` · ${s.grund}` : ''}
                      {s.selectline_hinweis && <div className="unter-mindestbestand">{s.selectline_hinweis}</div>}
                    </td>
                    <td>
                      <button disabled={arbeitet} onClick={() => void tun(() => api.sperrungAufheben(s.id))}>
                        Aufheben
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Pflegeabschnitt>

          <Pflegeabschnitt
            titel="Verwendbar bis (Fristen)"
            leer="keine in diesem Blatt"
            anzahl={fristenImBlatt.length}
            erklaerung="Grüner Punkt, RESY, PPWR, MHD: Bestand, der ab einem Datum nicht mehr in Verkehr gebracht werden darf. Die Frist ist die zweite Reichweitenzeile - sie gewinnt, wenn sie früher endet."
            formular={
              <div className="leiste felder">
                <label className="feld">
                  <span className="hinweis">Material</span>
                  <ArtikelAuswahl artikel={zeilen} wert={fristMaterial} onChange={setFristMaterial} />
                </label>
                <label className="feld">
                  <span className="hinweis">verwendbar bis</span>
                  <input type="date" value={fristBis} onChange={(e) => setFristBis(e.target.value)} />
                </label>
                <label className="feld">
                  <span className="hinweis">Grund</span>
                  <input
                    type="text"
                    style={{ width: 200 }}
                    placeholder="Grund"
                    value={fristGrund}
                    onKeyDown={beiEnter(fristSpeichern)}
                    onChange={(e) => setFristGrund(e.target.value)}
                  />
                </label>
                <label className="feld" title="So lange vor der Frist muss die Produktion beendet sein, weil die Ware noch verkauft wird.">
                  <span className="hinweis">Vorlauf (Produktion vorher beenden)</span>
                  <input
                    type="text"
                    style={{ width: 120 }}
                    placeholder="z.B. 4 Wo"
                    value={fristVorlauf}
                    onChange={(e) => setFristVorlauf(e.target.value)}
                  />
                </label>
                <label className="hinweis" title="Die Frist steht im Raum, ist aber noch nicht bestätigt - gerechnet wird trotzdem mit ihr.">
                  <input type="checkbox" checked={fristUngeklaert} onChange={(e) => setFristUngeklaert(e.target.checked)} /> noch ungeklärt
                </label>
                <button className="haupt" disabled={arbeitet} onClick={fristSpeichern}>
                  Frist setzen
                </button>
              </div>
            }
          >
            <table>
              <tbody>
                {fristenImBlatt.map((f) => (
                  <tr key={f.id}>
                    <td>
                      {f.material} {f.bezeichnung}
                    </td>
                    <td>
                      {f.verwendbar_kw} ({datum(f.verwendbar_bis)})
                      {f.vorlauf_tage ? (
                        <span className="hinweis"> - Produktion bis {datum(f.produktion_bis)} beenden ({wochen(f.vorlauf_tage)} Vorlauf)</span>
                      ) : null}
                    </td>
                    <td className="hinweis">
                      {f.grund}
                      {f.bemerkung && <div>{f.bemerkung}</div>}
                    </td>
                    <td>
                      {f.bestaetigt === false ? (
                        <span className="status gesperrt" title="Gerechnet wird trotzdem mit ihr">Frist ungeklärt</span>
                      ) : f.bestaetigt === true ? (
                        <span className="status gebucht">geklärt</span>
                      ) : null}
                    </td>
                    <td>
                      {f.bestaetigt === false && (
                        <button disabled={arbeitet} onClick={() => fristKlaeren(f, true)}>
                          Geklärt
                        </button>
                      )}
                      {f.bestaetigt === true && (
                        <button className="leise" disabled={arbeitet} onClick={() => fristKlaeren(f, false)}>
                          als ungeklärt vermerken
                        </button>
                      )}{' '}
                      <button disabled={arbeitet} onClick={() => void tun(() => api.fristAufheben(f.id))}>
                        Aufheben
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Pflegeabschnitt>

          <Vermerke artikel={vermerkArtikel} onGeaendert={laden} />
        </div>
      )}
    </>
  )
}
